import React, { useEffect, useRef, useState } from "react";
import {
  changeData,
  previousStep,
  useSteps,
} from "../../shared/features/subscription/stepsSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  useUploadCurriculoMutation,
  useUploadComprovanteMatriculaMutation,
  useUploadFileMutation,
} from "shared/features/api/inscricao/inscricaoSlice";
import {
  useGetCandidatoByCpfMutation,
  useGetCandidatoByEmailMutation,
  usePostCandidatoMutation,
} from "shared/features/api/candidato/candidatoSlice";
import { usePostInscricaoMutation } from "shared/features/api/inscricao/inscricaoSlice";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { SubscribeData } from "shared/interfaces";
import { IInscricaoBody } from "shared/features/api/inscricao/types";
import { trackTelemetryEvent } from "shared/utils/telemetry";


function clearFormSessionStorage() {
  sessionStorage.removeItem("stepOneFormData");
  sessionStorage.removeItem("stepTwoFormData");
  sessionStorage.removeItem("deficienciaBoolean");
  sessionStorage.removeItem("deficiencia");
}

export const StepThree: React.FC = () => {
  const dispatch = useDispatch();
  const { data } = useSelector(useSteps);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [shouldSubmitWithoutGeoOnRetry, setShouldSubmitWithoutGeoOnRetry] =
    useState<boolean>(false);
  const [geoFailureStatus, setGeoFailureStatus] = useState<string | null>(null);
  const [erroMsg, setErroMsg] = useState<string>("");
  const hasStartedRef = useRef(false);
  const attemptIdRef = useRef<string | null>(null);

  const [postInscricao] = usePostInscricaoMutation();
  const [uploeadCurriculo] = useUploadCurriculoMutation();
  const [uploadComprovanteMatricula] = useUploadComprovanteMatriculaMutation();
  const supportEmail = "vemser@dbccompany.com.br";

  const createAttemptId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const nowIso = () => new Date().toISOString();
  const supportMessage = `\n\nSe precisar de ajuda, entre em contato pelo e-mail oficial: ${supportEmail}.`;

  const renderErrorMessage = (message: string) => {
    if (!message.includes(supportEmail)) {
      return message;
    }

    const [before, after] = message.split(supportEmail);

    return (
      <>
        {before}
        <strong>{supportEmail}</strong>
        {after}
      </>
    );
  };

  const isGeolocationError = (
    error?: GeolocationPositionError | Error
  ): error is GeolocationPositionError => {
    return !!error && typeof error === "object" && "code" in error;
  };

  const getGeoErrorType = (error?: GeolocationPositionError | Error) => {
    if (error instanceof Error && error.message === "GEOLOCATION_NOT_SUPPORTED") {
      return "not_supported";
    }

    if (!isGeolocationError(error)) {
      return "unknown";
    }

    switch (error.code) {
      case 1:
        return "permission_denied";
      case 2:
        return "position_unavailable";
      case 3:
        return "timeout";
      default:
        return "unknown";
    }
  };

  const getGeoStatus = (error?: GeolocationPositionError | Error) => {
    if (error instanceof Error && error.message === "GEOLOCATION_NOT_SUPPORTED") {
      return "ERRO_NAVEGADOR_SEM_SUPORTE";
    }

    if (!isGeolocationError(error)) {
      return "ERRO_DESCONHECIDO";
    }

    switch (error.code) {
      case 1:
        return "ERRO_PERMISSAO_NEGADA";
      case 2:
        return "ERRO_POSICAO_INDISPONIVEL";
      case 3:
        return "ERRO_TIMEOUT";
      default:
        return "ERRO_DESCONHECIDO";
    }
  };

  const getGeoErrorMessage = (error?: GeolocationPositionError | Error) => {
    if (error instanceof Error && error.message === "GEOLOCATION_NOT_SUPPORTED") {
      return `Seu navegador não oferece suporte à geolocalização. Tente usar outro navegador para concluir a inscrição.${supportMessage}`;
    }

    if (!isGeolocationError(error)) {
      return `Não conseguimos coletar sua geolocalização. Verifique as permissões do navegador e tente novamente.${supportMessage}`;
    }

    switch (error.code) {
      case 1:
        return `A permissão de geolocalização foi negada. Habilite a localização no navegador e tente novamente.${supportMessage}`;
      case 2:
        return `Não foi possível obter sua localização no momento. Verifique sua conexão/rede e tente novamente.${supportMessage}`;
      case 3:
        return `Não foi possível concluir a coleta da sua localização dentro do tempo esperado. Certifique-se de que o aviso de permissão do navegador para a coleta dos dados de localização foi exibido e de que a permissão foi concedida. Caso o aviso não tenha sido aberto, confirme se a permissão de localização está habilitada no navegador e tente novamente em alguns segundos.${supportMessage}`;
      default:
        return `Não conseguimos coletar sua geolocalização. Verifique as permissões do navegador e tente novamente.${supportMessage}`;
    }
  };

  const getGeolocationWithTimeout = (timeout = 5000) =>
    new Promise<{ lat: string; long: string }>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("GEOLOCATION_NOT_SUPPORTED"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: String(position.coords.latitude),
            long: String(position.coords.longitude),
          });
        },
        (error) => reject(error),
        {
          timeout,
          enableHighAccuracy: false,
          maximumAge: 0,
        }
      );
    });

  async function handleSubmitAll(
    data: SubscribeData,
    geoOverride?: {
      audLat: string | null;
      audLong: string | null;
      geolocalizacaoStatus: string;
    }
  ) {
    try {
      setIsLoading(true);

      // 1. Cadastra inscrição
      const formData: IInscricaoBody = {
        candidato: {
          nome: data.nome,
          dataNascimento: data.dataNascimento,
          email: data.email,
          telefone: data.telefone,
          cpf: data.cpf,
          estado: data.estado,
          cidade: data.cidade,
          pcd: data.deficiencia,
          neurodiversidade: data.neurodiversidade,
          genero: data.genero,
        },
        matriculadoBoolean: data.matriculado,
        curso: data.curso.trim(),
        turno: data.turno,
        instituicao: data.instituicao.trim(),
        github: data.github,
        linkedin: data.linkedin,
        resposta: data.resposta,
        lgpdBoolean: data.lgpdBoolean,
        polPrivacidade: data.polPrivacidade,
        provaBoolean: data.provaBoolean,
        ingles: data.ingles,
        espanhol: data.espanhol,
        efetivacaoBoolean: data.efetivacaoBoolean,
        disponibilidadeBoolean: data.disponibilidadeBoolean,
        primeiraOpcaoTrilha: data.trilha1.toUpperCase(),
        segundaOpcaoTrilha: data.trilha2.toUpperCase(),
        importancia: data.algoimportante,
        qtdSemestres: parseInt(data.qtdSemestres),
        semestreAtual: parseInt(data.semestreAtual),
        tipoGraduacao: data.tipoGraduacao,
        descConfigPc: data.descConfigPc,
        linguagens: data.linguagens,
        linkEdital: data.linkEdital,
        comoSoube: data.comoSoube,
        audLat: geoOverride?.audLat ?? data.audLat,
        audLong: geoOverride?.audLong ?? data.audLong,
        geolocalizacaoStatus: geoOverride?.geolocalizacaoStatus ?? "COLETADA"
      };

     
     //console.log(formData);

      const inscricaoRes = await postInscricao({
        body: formData,
      }).unwrap();

      const idInscricao = inscricaoRes.idInscricao;

      await Promise.all([
        uploeadCurriculo({
          file: (() => {
            const f = new FormData();
            f.append("file", data.curriculo[0]);
            return f;
          })(),
          idInscricao,
        }).unwrap(),
        uploadComprovanteMatricula({
          file: (() => {
            const f = new FormData();
            f.append("file", data.comprovanteMatricula[0]);
            return f;
          })(),
          idInscricao,
        }).unwrap(),
      ]);
      clearFormSessionStorage();
      setIsError(false);
      setShouldSubmitWithoutGeoOnRetry(false);
      setGeoFailureStatus(null);
    } catch (err: any) {
      console.error(err);
      let mensagens = Array.isArray(err?.data?.errors)
        ? err.data.errors.flat().join("\n")
        : "Erro desconhecido";

      if (/audLat.*n[ãa]o deve ser nulo|audLat: must not be null/i.test(mensagens) || /audLong.*n[ãa]o deve ser nulo|audLong: must not be null/i.test(mensagens)) {
        mensagens = "Ops! Não conseguimos coletar sua geolocalização. Por favor, verifique nas notificações do navegador ou permita que pop-ups apareçam, para confirmar sua geolocalização. Caso o erro persista, tente em outro navegador.";
      }

      setErroMsg(mensagens);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function submitWithoutGeolocation(explicitAttemptId?: string) {
    const attemptId = explicitAttemptId || createAttemptId();
    attemptIdRef.current = attemptId;

    if (!data) {
      setErroMsg("Não foi possível carregar os dados da inscrição. Volte e tente novamente.");
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!data.polPrivacidade) {
      setErroMsg("É necessário aceitar os termos de tratamento de dados e geolocalização para concluir a inscrição.");
      setIsError(true);
      setIsLoading(false);
      return;
    }

    trackTelemetryEvent("geo_retry_submit_without_geo", {
      attemptId,
      flowStep: "step_three",
      timestamp: nowIso(),
    });

    await handleSubmitAll(data, {
      audLat: "0",
      audLong: "0",
      geolocalizacaoStatus: geoFailureStatus ?? "ERRO_DESCONHECIDO",
    });
  }

  async function submitWithGeolocation(explicitAttemptId?: string) {
    const attemptId = explicitAttemptId || createAttemptId();
    attemptIdRef.current = attemptId;

    if (!data) {
      setErroMsg("Não foi possível carregar os dados da inscrição. Volte e tente novamente.");
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!data.polPrivacidade) {
      setErroMsg("É necessário aceitar os termos de tratamento de dados e geolocalização para concluir a inscrição.");
      setIsError(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setShouldSubmitWithoutGeoOnRetry(false);
    setGeoFailureStatus(null);
    setErroMsg("");

    const start = performance.now();

    trackTelemetryEvent("geo_prompt_started", {
      attemptId,
      flowStep: "step_three",
      timestamp: nowIso(),
      browser: navigator.userAgent,
    });

    try {
      const geo = await getGeolocationWithTimeout(10000);
      const elapsedMs = Math.round(performance.now() - start);

      trackTelemetryEvent("geo_capture_success", {
        attemptId,
        elapsedMs,
        flowStep: "step_three",
        timestamp: nowIso(),
      });

      dispatch(
        changeData({
          audLat: geo.lat,
          audLong: geo.long,
        })
      );

      await handleSubmitAll({
        ...data,
        audLat: geo.lat,
        audLong: geo.long,
      });
    } catch (error: any) {
      const elapsedMs = Math.round(performance.now() - start);
      const errorType = getGeoErrorType(error);
      const geolocalizacaoStatus = getGeoStatus(error);

      trackTelemetryEvent("geo_capture_failure", {
        attemptId,
        errorType,
        errorCode: typeof error?.code === "number" ? error.code : -1,
        elapsedMs,
        flowStep: "step_three",
        timestamp: nowIso(),
      });

      trackTelemetryEvent("submit_blocked_by_geo", {
        attemptId,
        blockReason: errorType,
        geolocalizacaoStatus,
        flowStep: "step_three",
        timestamp: nowIso(),
      });

      setShouldSubmitWithoutGeoOnRetry(true);
      setGeoFailureStatus(geolocalizacaoStatus);

      setErroMsg(getGeoErrorMessage(error));
      setIsError(true);
      setIsLoading(false);
    }
  }

  const handleRetry = () => {
    const previousAttemptId = attemptIdRef.current;
    const newAttemptId = createAttemptId();

    trackTelemetryEvent("geo_retry_clicked", {
      previousAttemptId,
      newAttemptId,
      flowStep: "step_three",
      timestamp: nowIso(),
    });

    if (shouldSubmitWithoutGeoOnRetry) {
      submitWithoutGeolocation(newAttemptId);
      return;
    }

    submitWithGeolocation(newAttemptId);
  };

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    submitWithGeolocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            mb: 2,
          }}
        >
          <CircularProgress />
          <p>Sua incrição está sendo processada. Por favor, aguarde.</p>
        </Box>
      )}
      {isError && (
        <Box>
          <Alert severity="warning">
            <AlertTitle>Houve algum erro!</AlertTitle>
            <Box sx={{ whiteSpace: "pre-line" }}>
              {renderErrorMessage(
                erroMsg || "Houve algum erro ao enviar os dados da inscrição."
              )}
            </Box>
          </Alert>
          <Button
            variant="contained"
            sx={{
              mt: 2,
              mr: 2,
            }}
            onClick={handleRetry}
            disabled={isLoading}
          >
            Tentar novamente
          </Button>
          <Button
            variant="outlined"
            sx={{
              mt: 2,
            }}
            onClick={() => {
              dispatch(previousStep());
            }}
          >
            Voltar
          </Button>
        </Box>
      )}
      {isLoading === false && isError === false && (
        <Alert severity="success">
          <AlertTitle>
            Inscrição realizada com sucesso!
          </AlertTitle>
          <br />
          Agradecemos sua inscrição! Agora, é fundamental que você fique atento(a) ao seu e-mail de cadastro, incluindo a caixa de spam. Você receberá por lá o link para o nosso questionário comportamental, e ele <strong>deve ser respondido no prazo de até 8 horas</strong> para darmos sequência ao seu processo seletivo.
          <br />
          Caso você não receba o e-mail em até 30 minutos, entre em contato pelo e-mail oficial: <strong>vemser@dbccompany.com.br</strong>.
          <br />
          Não se esqueça de conferir o cronograma completo no edital.
        </Alert>
      )}
    </>
  );
};
